import apiClient from "./api";
import {
  Fornecedor,
  CreateFornecedorDTO,
  UpdateFornecedorDTO,
  PaginationParams,
  ApiResponse,
  PaginatedResponse,
} from "../types";

export const fornecedorService = {
  async getAll(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Fornecedor>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<Fornecedor>>
    >(`/fornecedores?${queryParams.toString()}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao buscar fornecedores");
    }

    return response.data.data;
  },

  async getById(id: string): Promise<Fornecedor> {
    const response = await apiClient.get<ApiResponse<Fornecedor>>(
      `/fornecedores/${id}`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Fornecedor não encontrado");
    }

    return response.data.data;
  },

  async create(fornecedor: CreateFornecedorDTO): Promise<Fornecedor> {
    const response = await apiClient.post<ApiResponse<Fornecedor>>(
      "/fornecedores",
      fornecedor,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao criar fornecedor");
    }

    return response.data.data;
  },

  async createFromReceita(cnpj: string): Promise<Fornecedor> {
    const response = await apiClient.post<ApiResponse<Fornecedor>>(
      "/fornecedores/from-receita",
      { cnpj },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || "Erro ao buscar dados da Receita Federal",
      );
    }

    return response.data.data;
  },

  async update(
    id: string,
    fornecedor: UpdateFornecedorDTO,
  ): Promise<Fornecedor> {
    const response = await apiClient.put<ApiResponse<Fornecedor>>(
      `/fornecedores/${id}`,
      fornecedor,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao atualizar fornecedor");
    }

    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/fornecedores/${id}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Erro ao deletar fornecedor");
    }
  },

  async syncFromWrike(): Promise<{
    totalProcessed: number;
    totalReceived: number;
    nomesFantasiaAtualizados?: number;
    tempoDecorrido: string;
    velocidade: string;
  }> {
    const response = await apiClient.post<
      ApiResponse<{
        totalProcessed: number;
        totalReceived: number;
        nomesFantasiaAtualizados?: number;
        tempoDecorrido: string;
        velocidade: string;
      }>
    >("/fornecedores/sincronizar-wrike");

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao sincronizar com Wrike");
    }

    return response.data.data;
  },

  /**
   * Preenche o nome fantasia de quem está sem, consultando a BrasilAPI.
   *
   * Um lote por chamada, para a requisição não ficar pendurada; o servidor
   * paraleliza dentro do lote. `aindaAmbiguos` diz quantos restam, para a tela
   * saber se deve continuar.
   */
  async buscarNomesFantasia(limite = 100): Promise<{
    processados: number;
    atualizados: number;
    semFantasia: number;
    naoEncontrado: number;
    cnpjInvalido: number;
    limiteAtingido: number;
    erros: number;
    aindaAmbiguos: number;
    deveAguardar: boolean;
    exemplos: Array<{ id: string; nome: string; nomeFantasia: string }>;
  }> {
    const response = await apiClient.post<
      ApiResponse<{
        processados: number;
        atualizados: number;
        semFantasia: number;
        naoEncontrado: number;
        cnpjInvalido: number;
        limiteAtingido: number;
        erros: number;
        aindaAmbiguos: number;
        deveAguardar: boolean;
        exemplos: Array<{ id: string; nome: string; nomeFantasia: string }>;
      }>
    >(`/painel/fornecedores/backfill-nome-fantasia?limite=${limite}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || "Erro ao buscar os nomes fantasia",
      );
    }

    return response.data.data;
  },

  async syncFromReceita(id: string): Promise<Fornecedor> {
    try {
      const response = await apiClient.put<ApiResponse<Fornecedor>>(
        `/fornecedores/${id}/sincronizar`,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.message || "Erro ao sincronizar com a Receita Federal",
        );
      }

      return response.data.data;
    } catch (err: any) {
      // Extrai a mensagem amigável do body retornado pela API (erros 4xx/5xx)
      const apiMessage: string | undefined = err?.response?.data?.message;
      throw new Error(
        apiMessage ||
          err?.message ||
          "Erro ao sincronizar com a Receita Federal",
      );
    }
  },
};
